// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Users,
  BarChart3,
  Clock,
  Sparkles,
  CheckCircle2,
  HelpCircle,
  BookOpen,
  MessageSquare,
  Bell,
  ArrowLeft,
  Smartphone,
} from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { SEOHead } from "../components/SEOHead";
import { PublicFooter } from "../components/PublicFooter";

/**
 * Page de documentation pour DooDates1 (Sondages de Dates)
 * Style moderne inspiré de la documentation Quiz
 */
export default function DatePollsDocumentation() {
  const navigate = useNavigate();
  const { t } = useTranslation(["polls", "common", "legal"]);
  return (
    <div className="flex flex-col min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <SEOHead title={t("common:seo_title_docs")} />
      <ProductHeader productType="date" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="date" />
        <div className="flex-1">
          {/* Hero Section */}
          <section className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <span className="text-sm text-primary">
                    {t("polls:doc_user_guide_badge")}
                  </span>
                </div>
                <h1 className="text-4xl font-extrabold text-foreground mb-4">
                  {t("polls:doc_title_prefix")}{" "}
                  <span className="text-primary">
                    {t("polls:doc_title_highlight")}
                  </span>
                </h1>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  {t("polls:doc_intro")}
                </p>
              </div>

              {/* Quick Links */}
              <div className="grid md:grid-cols-4 gap-6 mb-16">
                <Link
                  to="#getting-started"
                  className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                >
                  <Sparkles className="w-8 h-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold mb-2">
                    {t("polls:doc_quicklink_getting_started_title")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("polls:doc_quicklink_getting_started_desc")}
                  </p>
                </Link>
                <Link
                  to="#creating-polls"
                  className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                >
                  <Calendar className="w-8 h-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold mb-2">
                    {t("polls:doc_quicklink_create_title")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("polls:doc_quicklink_create_desc")}
                  </p>
                </Link>
                <Link
                  to="#managing-responses"
                  className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                >
                  <Users className="w-8 h-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold mb-2">
                    {t("polls:doc_quicklink_responses_title")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("polls:doc_quicklink_responses_desc")}
                  </p>
                </Link>
                <Link
                  to="#advanced-features"
                  className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                >
                  <BarChart3 className="w-8 h-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold mb-2">
                    {t("polls:doc_quicklink_advanced_title")}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t("polls:doc_quicklink_advanced_desc")}
                  </p>
                </Link>
              </div>

              {/* Documentation Content */}
              <div className="max-w-4xl mx-auto space-y-16">
                {/* Getting Started */}
                <section id="getting-started" className="scroll-mt-8">
                  <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                    <Sparkles className="w-8 h-8 text-primary" />
                    {t("polls:doc_getting_started_heading")}
                  </h2>

                  <div className="bg-card border border-border rounded-xl p-8">
                    <h3 className="text-xl font-semibold text-foreground mb-4">
                      {t("polls:doc_what_is_heading")}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {t("polls:doc_what_is_body")}
                    </p>

                    <h3 className="text-xl font-semibold text-foreground mb-4 mt-8">
                      {t("polls:doc_create_account_heading")}
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-primary font-semibold text-sm">
                            1
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground mb-1">
                            {t("polls:doc_account_step1_title")}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {t("polls:doc_account_step1_desc")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-primary font-semibold text-sm">
                            2
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground mb-1">
                            {t("polls:doc_account_step2_title")}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {t("polls:doc_account_step2_desc")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-primary font-semibold text-sm">
                            3
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-foreground mb-1">
                            {t("polls:doc_account_step3_title")}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {t("polls:doc_account_step3_desc")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Creating Polls */}
                <section id="creating-polls" className="scroll-mt-8">
                  <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                    <Calendar className="w-8 h-8 text-primary" />
                    {t("polls:doc_create_heading")}
                  </h2>

                  <div className="bg-card border border-border rounded-xl p-8">
                    <h3 className="text-xl font-semibold text-foreground mb-4">
                      {t("polls:doc_step_by_step_heading")}
                    </h3>

                    <div className="space-y-6">
                      <div className="border-l-4 border-primary pl-6">
                        <h4 className="font-medium text-foreground mb-2">
                          {t("polls:doc_create_step1_title")}
                        </h4>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_create_step1_desc")}
                        </p>
                        <div className="bg-muted rounded-lg p-4 text-sm">
                          <p className="text-muted-foreground mb-2">
                            <strong>
                              {t("polls:doc_create_step1_example_title_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step1_example_title_value")}
                          </p>
                          <p className="text-muted-foreground">
                            <strong>
                              {t("polls:doc_create_step1_example_desc_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step1_example_desc_value")}
                          </p>
                        </div>
                      </div>

                      <div className="border-l-4 border-primary pl-6">
                        <h4 className="font-medium text-foreground mb-2">
                          {t("polls:doc_create_step2_title")}
                        </h4>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_create_step2_desc")}
                        </p>
                        <ul className="text-muted-foreground text-sm space-y-1">
                          <li>{t("polls:doc_create_step2_item1")}</li>
                          <li>{t("polls:doc_create_step2_item2")}</li>
                        </ul>
                      </div>

                      <div className="border-l-4 border-primary pl-6">
                        <h4 className="font-medium text-foreground mb-2">
                          {t("polls:doc_create_step3_title")}
                        </h4>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_create_step3_desc")}
                        </p>
                        <ul className="text-muted-foreground text-sm space-y-1">
                          <li>
                            •{" "}
                            <strong>
                              {t("polls:doc_create_step3_item1_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step3_item1_text")}
                          </li>
                          <li>
                            •{" "}
                            <strong>
                              {t("polls:doc_create_step3_item2_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step3_item2_text")}
                          </li>
                          <li>
                            •{" "}
                            <strong>
                              {t("polls:doc_create_step3_item3_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step3_item3_text")}
                          </li>
                        </ul>
                      </div>

                      <div className="border-l-4 border-primary pl-6">
                        <h4 className="font-medium text-foreground mb-2">
                          {t("polls:doc_create_step4_title")}
                        </h4>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_create_step4_desc")}
                        </p>
                        <ul className="text-muted-foreground text-sm space-y-1">
                          <li>
                            •{" "}
                            <strong>
                              {t("polls:doc_create_step4_item1_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step4_item1_text")}
                          </li>
                          <li>
                            •{" "}
                            <strong>
                              {t("polls:doc_create_step4_item2_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step4_item2_text")}
                          </li>
                          <li>
                            •{" "}
                            <strong>
                              {t("polls:doc_create_step4_item3_label")}
                            </strong>{" "}
                            {t("polls:doc_create_step4_item3_text")}
                          </li>
                        </ul>
                      </div>

                      <div className="border-l-4 border-primary pl-6">
                        <h4 className="font-medium text-foreground mb-2">
                          {t("polls:doc_create_step5_title")}
                        </h4>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_create_step5_desc")}
                        </p>
                        <ul className="text-muted-foreground text-sm space-y-1">
                          <li>{t("polls:doc_create_step5_item1")}</li>
                          <li>{t("polls:doc_create_step5_item2")}</li>
                          <li>{t("polls:doc_create_step5_item3")}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Managing Responses */}
                <section id="managing-responses" className="scroll-mt-8">
                  <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                    <Users className="w-8 h-8 text-primary" />
                    {t("polls:doc_responses_heading")}
                  </h2>

                  <div className="bg-card border border-border rounded-xl p-8">
                    <h3 className="text-xl font-semibold text-foreground mb-4">
                      {t("polls:doc_dashboard_heading")}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {t("polls:doc_dashboard_body")}
                    </p>

                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="bg-muted rounded-lg p-6">
                        <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-primary" />
                          {t("polls:doc_live_stats_heading")}
                        </h4>
                        <ul className="text-muted-foreground text-sm space-y-2">
                          <li>{t("polls:doc_live_stats_item1")}</li>
                          <li>{t("polls:doc_live_stats_item2")}</li>
                          <li>{t("polls:doc_live_stats_item3")}</li>
                          <li>{t("polls:doc_live_stats_item4")}</li>
                        </ul>
                      </div>
                      <div className="bg-muted rounded-lg p-6">
                        <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-success" />
                          {t("polls:doc_available_actions_heading")}
                        </h4>
                        <ul className="text-muted-foreground text-sm space-y-2">
                          <li>{t("polls:doc_available_actions_item1")}</li>
                          <li>{t("polls:doc_available_actions_item2")}</li>
                          <li>{t("polls:doc_available_actions_item3")}</li>
                          <li>{t("polls:doc_available_actions_item4")}</li>
                        </ul>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-foreground mb-4">
                      {t("polls:doc_export_heading")}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t("polls:doc_export_body")}
                    </p>
                    <ul className="text-muted-foreground text-sm space-y-1">
                      <li>
                        • <strong>{t("polls:doc_export_csv_label")}</strong>{" "}
                        {t("polls:doc_export_csv_text")}
                      </li>
                      <li>
                        • <strong>{t("polls:doc_export_pdf_label")}</strong>{" "}
                        {t("polls:doc_export_pdf_text")}
                      </li>
                      <li>
                        • <strong>{t("polls:doc_export_json_label")}</strong>{" "}
                        {t("polls:doc_export_json_text")}
                      </li>
                      <li>
                        •{" "}
                        <strong>{t("polls:doc_export_markdown_label")}</strong>{" "}
                        {t("polls:doc_export_markdown_text")}
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Advanced Features */}
                <section id="advanced-features" className="scroll-mt-8">
                  <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                    <Bell className="w-8 h-8 text-primary" />
                    {t("polls:doc_advanced_heading")}
                  </h2>

                  <div className="bg-card border border-border rounded-xl p-8">
                    <h3 className="text-xl font-semibold text-foreground mb-4">
                      {t("polls:doc_notifications_heading")}
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {t("polls:doc_notifications_body")}
                    </p>

                    <div className="space-y-4 mb-8">
                      <div className="flex items-start gap-4">
                        <MessageSquare className="w-5 h-5 text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-foreground mb-1">
                            {t("polls:doc_notif_email_title")}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {t("polls:doc_notif_email_desc")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <Clock className="w-5 h-5 text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-foreground mb-1">
                            {t("polls:doc_notif_reminders_title")}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {t("polls:doc_notif_reminders_desc")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <HelpCircle className="w-5 h-5 text-primary mt-1" />
                        <div>
                          <h4 className="font-medium text-foreground mb-1">
                            {t("polls:doc_notif_closing_title")}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {t("polls:doc_notif_closing_desc")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Installer sur mobile */}
                <section id="mobile-install" className="scroll-mt-8">
                  <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                    <Smartphone className="w-8 h-8 text-primary" />
                    {t("polls:doc_mobile_heading")}
                  </h2>

                  <div className="bg-card border border-border rounded-xl p-8">
                    <p className="text-muted-foreground mb-6">
                      {t("polls:doc_mobile_intro")}
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-muted border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-3">
                          {t("polls:doc_mobile_android_title")}
                        </h3>
                        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                          <li>
                            {t("polls:doc_mobile_android_step1_prefix")}{" "}
                            <strong className="text-foreground">
                              doodates.com
                            </strong>{" "}
                            {t("polls:doc_mobile_android_step1_suffix")}
                          </li>
                          <li>{t("polls:doc_mobile_android_step2")}</li>
                          <li>
                            {t("polls:doc_mobile_android_step3_prefix")}{" "}
                            <strong className="text-foreground">
                              {t("polls:doc_mobile_android_step3_option")}
                            </strong>
                          </li>
                          <li>{t("polls:doc_mobile_android_step4")}</li>
                        </ol>
                      </div>

                      <div className="bg-muted border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-3">
                          {t("polls:doc_mobile_ios_title")}
                        </h3>
                        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                          <li>
                            {t("polls:doc_mobile_ios_step1_prefix")}{" "}
                            <strong className="text-foreground">
                              doodates.com
                            </strong>{" "}
                            {t("polls:doc_mobile_ios_step1_suffix")}
                          </li>
                          <li>
                            {t("polls:doc_mobile_ios_step2_prefix")}{" "}
                            <strong className="text-foreground">
                              {t("polls:doc_mobile_ios_step2_share")}
                            </strong>{" "}
                            {t("polls:doc_mobile_ios_step2_suffix")}
                          </li>
                          <li>
                            {t("polls:doc_mobile_ios_step3_prefix")}{" "}
                            <strong className="text-foreground">
                              {t("polls:doc_mobile_ios_step3_option")}
                            </strong>
                          </li>
                          <li>
                            {t("polls:doc_mobile_ios_step4_prefix")}{" "}
                            <strong className="text-foreground">
                              {t("polls:doc_mobile_ios_step4_add")}
                            </strong>
                          </li>
                        </ol>
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm mt-6">
                      {t("polls:doc_mobile_outro")}
                    </p>
                  </div>
                </section>

                {/* FAQ */}
                <section className="scroll-mt-8">
                  <h2 className="text-3xl font-bold text-foreground mb-8">
                    {t("polls:doc_faq_heading")}
                  </h2>

                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h4 className="font-medium text-foreground mb-2">
                        {t("polls:doc_faq_q1")}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {t("polls:doc_faq_a1")}
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h4 className="font-medium text-foreground mb-2">
                        {t("polls:doc_faq_q2")}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {t("polls:doc_faq_a2")}
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h4 className="font-medium text-foreground mb-2">
                        {t("polls:doc_faq_q3")}
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        {t("polls:doc_faq_a3")}
                      </p>
                    </div>
                  </div>
                </section>
              </div>

              {/* Documents disponibles */}
              <section id="documents" className="scroll-mt-8">
                <h2 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
                  <BookOpen className="w-8 h-8 text-primary" />
                  {t("polls:doc_documents_heading")}
                </h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <Link
                    to="/docs/privacy"
                    className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                        <span className="text-primary font-bold">P</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {t("legal:privacy_policy_title")}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_doc_privacy_desc")}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>privacy.md</span>
                          <span>·</span>
                          <span>{t("polls:doc_doc_type_legal")}</span>
                        </div>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/docs/mentions-legales"
                    className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                        <span className="text-primary font-bold">M</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {t("legal:legal_notice_title")}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_doc_legal_notice_desc")}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>mentions-legales.md</span>
                          <span>·</span>
                          <span>{t("polls:doc_doc_type_legal")}</span>
                        </div>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/docs/ia-policy"
                    className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                        <span className="text-primary font-bold">
                          {t("polls:doc_badge_ai")}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {t("polls:doc_doc_ai_policy_title")}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_doc_ai_policy_desc")}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>ia-policy.md</span>
                          <span>·</span>
                          <span>{t("polls:doc_doc_type_technical")}</span>
                        </div>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/docs/cg-u"
                    className="p-6 bg-card border border-border rounded-xl hover:bg-accent transition-colors group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/30 transition-colors">
                        <span className="text-primary font-bold">
                          {t("polls:doc_badge_terms")}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                          {t("polls:doc_doc_terms_title")}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3">
                          {t("polls:doc_doc_terms_desc")}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>cg-u.md</span>
                          <span>·</span>
                          <span>{t("polls:doc_doc_type_contractual")}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </section>

              {/* CTA Section */}
              <div className="mt-16 text-center">
                <div className="bg-primary/10 rounded-2xl p-8 border border-primary/20">
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    {t("polls:doc_cta_heading")}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {t("polls:doc_cta_body")}
                  </p>
                  <Link
                    to="/workspace/date"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Calendar className="w-5 h-5" />
                    {t("polls:create_poll")}
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Retour */}
          <div className="pb-12 text-center">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors px-4 py-2 rounded-lg hover:bg-accent"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common:back")}
            </button>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
